import { supabase } from '../lib/supabaseClient';
import { isDemoMode } from '../demo/demoMode';
import { getUserInfo } from '../utils/tokenManager';
import { getAuthInfo } from './authService';

/* 담당자 정산 확인 (Supabase group_manager_check)
 * ===========================================================================
 * ERP 에 칸이 없어서 단체번호를 키로 Supabase 에 따로 저장한다.
 * - 조회 실패/미설정이면 "미확인" 으로 보고 화면 흐름을 막지 않는다.
 * - 데모 모드에서는 Supabase 로 나가지 않는다 (저장은 화면 상태로만).
 */

const toGroupNo = (v) => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
};

/** 여러 단체의 확인 여부를 한 번에 읽는다. → Map<단체번호(number), row> */
export const getGroupManagerChecks = async (groupNos = []) => {
  const result = new Map();
  if (isDemoMode() || !supabase) return result;
  const nos = Array.from(new Set((groupNos || []).map(toGroupNo).filter(Boolean)));
  if (!nos.length) return result;

  // in() 목록이 너무 길면 URL 이 길어지므로 나눠서 읽는다
  const CHUNK = 200;
  for (let i = 0; i < nos.length; i += CHUNK) {
    const { data, error } = await supabase
      .from('group_manager_check')
      .select('단체번호, 확인여부, 확인자, 확인일시')
      .in('단체번호', nos.slice(i, i + CHUNK));
    if (error) {
      console.warn('[groupManagerCheck] 조회 실패(무시):', error.message);
      return result;
    }
    (data || []).forEach((row) => result.set(Number(row.단체번호), row));
  }
  return result;
};

/** 단체 하나의 확인 여부. 없으면 null */
export const getGroupManagerCheck = async (groupNo) => {
  const no = toGroupNo(groupNo);
  if (!no) return null;
  const map = await getGroupManagerChecks([no]);
  return map.get(no) || null;
};

/**
 * 확인 여부 저장 (upsert). 실패 시 throw — 호출부가 체크 상태를 되돌린다.
 * @returns 저장된 row (데모 모드면 저장 없이 만든 row)
 */
export const saveGroupManagerCheck = async ({ groupNo, checked }) => {
  const no = toGroupNo(groupNo);
  if (!no) throw new Error('단체번호가 없어 저장할 수 없습니다.');

  const { userNo, companyNo, email } = getUserInfo() || {};
  const loginId = getAuthInfo()?.userName || '';
  const row = {
    단체번호: no,
    공급업체번호: toGroupNo(companyNo),
    확인여부: !!checked,
    확인자번호: checked ? String(userNo || '') : null,
    확인자: checked ? (loginId || email || '') : null,
    확인일시: checked ? new Date().toISOString() : null,
    updated_at: new Date().toISOString(),
  };

  if (isDemoMode()) return row;
  if (!supabase) throw new Error('Supabase 설정(REACT_APP_SUPABASE_URL / ANON_KEY)이 없어 저장할 수 없습니다.');

  const { error } = await supabase.from('group_manager_check').upsert(row, { onConflict: '단체번호' });
  if (error) throw new Error(`담당자 확인 저장 실패: ${error.message}`);
  return row;
};
